import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Button, Segmented, Space, Spin, Tag, Typography, message } from 'antd';
import {
  ArrowLeftOutlined, FilePdfOutlined, FileImageOutlined, PrinterOutlined,
  InboxOutlined, BankOutlined, DownloadOutlined,
} from '@ant-design/icons';
import './ReportPage.css';
import { getProject, getDaltResults, getEbtronResults, getPowerResults, getFacilityAssessments, getBoilerECMResults } from '../firebase/firestore';
import { getPMSchedules, getWorkOrders } from '../firebase/pmScheduler';
import { getComplianceRecords } from '../firebase/compliance';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { RATING_TAG, getBuildingRating, maxPossibleScore, isAssetAssessed, generateRecommendations } from '../modules/facility/facilityCalculations';
import { exportSystemCsv } from '../modules/facility/facilityCsv';
import {
  ReportData, SYSTEM_LABELS, SYSTEM_KEYS, LOGO_SRC, MARK_SRC, MARK_WHITE_SRC, PhotoMap,
  buildReportPdf, buildDetailedReportPdf, loadImage, rasterize,
  reportFileName, detailedReportFileName, capitalPlanRows,
} from './reportPdf';
import { SectionTitle, TONE } from './reportChrome';
import ReportDetailedView from './ReportDetailedView';

/* Preset tags, not white-on-solid-hex. RATING_COLORS is the old Bootstrap palette and white
   text on it fails badly — Poor #ffc107 is 1.6:1, Good #0dcaf0 is 2.0:1 — and in print, where
   browsers drop backgrounds by default, every one of them became white on white. The presets
   are a light tint with darker text of the same hue, so they stay readable even with the
   background stripped, and they match how ratings already render in the facility module. */
const ratingTag = (rating: string | undefined, size: 'sm' | 'md' = 'md') =>
  rating ? (
    <Tag
      color={RATING_TAG[rating as keyof typeof RATING_TAG]}
      style={{ marginInlineEnd: 0, fontSize: size === 'sm' ? 11 : undefined }}
    >
      {rating}
    </Tag>
  ) : null;

/* Description (the equipment type) is a required field; a report may not be generated or exported
   while any assessed asset is missing one. Returns one label per offending asset (building + a
   human name) for the blocking notice. */
const assetsMissingDescription = (assessments: ReportData['facilityAssessments']): string[] =>
  assessments.flatMap(fa =>
    SYSTEM_KEYS.flatMap(k => fa[k].assets
      .filter(a => !a.description || !a.description.trim())
      .map(a => `${fa.buildingName || 'Untitled'}: ${a.assetId || 'Unnamed asset'}`)));

const ReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { orgId, organization, isLoading: orgLoading } = useOrg();
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState<'summary' | 'detailed' | null>(null);
  const [view, setView] = useState<'summary' | 'detailed'>('summary');

  /* Two-phase load. Both reports are scoped to the facility assessment, so the page renders as soon
     as the two datasets that matter — the project and its assessments — are in hand (both cache-
     first, so instant on a revisit). The seven other-module datasets used to be awaited in the same
     Promise.all: on weak Wi-Fi the report sat on the slowest of nine server round-trips, and one
     timeout re-fetched all nine. They now load in the background after first paint and merge into
     the page as they arrive; a failure leaves that section empty rather than blocking the report.

     Gated on OrgContext (token live — a cold PWA relaunch onto a report route otherwise reads before
     auth attaches; and orgId is populated before the access check, so a null orgId never reads as
     "Access denied"). `slow` gates the spinner so a millisecond cache hit doesn't flash one. */
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!id || orgLoading) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const slowTimer = setTimeout(() => { if (!cancelled) setSlow(true); }, 300);

    // Merge one background dataset into the page; used for both the cached copy and the refresh.
    const merge = <K extends keyof ReportData>(key: K) => (v: ReportData[K]) => {
      if (!cancelled) setData(d => (d ? { ...d, [key]: v } : d));
    };

    // The report is read-only, so it always applies the fresh facility copy (via merge) — this is
    // how it self-corrects when assets were added on another device since the cache was last warm.
    Promise.all([getProject(id), getFacilityAssessments(id, merge('facilityAssessments'))])
      .then(([project, facility]) => {
        if (cancelled) return;
        if (!project) { setError('Project not found'); return; }
        // Access check: must be project owner or member of the project's org
        const isOwner = project.userId === currentUser?.uid;
        const isOrgMember = project.orgId != null && project.orgId === orgId;
        if (!isOwner && !isOrgMember) { setError('Access denied'); return; }
        setData({
          project, facilityAssessments: facility,
          daltResults: [], ebtronResults: [], powerResults: [], boilerECMResults: [],
          pmSchedules: [], workOrders: [], complianceRecords: [],
        });
        // Phase 2 — the other-module sections, after first paint. Never block, never fail the page.
        getDaltResults(id, merge('daltResults')).then(merge('daltResults')).catch(() => {});
        getEbtronResults(id, merge('ebtronResults')).then(merge('ebtronResults')).catch(() => {});
        getPowerResults(id, merge('powerResults')).then(merge('powerResults')).catch(() => {});
        getBoilerECMResults(id, merge('boilerECMResults')).then(merge('boilerECMResults')).catch(() => {});
        getPMSchedules(id, merge('pmSchedules')).then(merge('pmSchedules')).catch(() => {});
        getWorkOrders(id, merge('workOrders')).then(merge('workOrders')).catch(() => {});
        getComplianceRecords(id, merge('complianceRecords')).then(merge('complianceRecords')).catch(() => {});
      })
      .catch(() => { if (!cancelled) setError('Failed to load report data'); })
      .finally(() => { clearTimeout(slowTimer); if (!cancelled) { setLoading(false); setSlow(false); } });
    return () => { cancelled = true; clearTimeout(slowTimer); };
  }, [id, currentUser, orgId, orgLoading]);

  const preparedBy = () =>
    organization?.name || currentUser?.displayName || currentUser?.email || '—';

  const generatePDF = async () => {
    if (!data || pdfBusy) return;
    if (assetsMissingDescription(data.facilityAssessments).length) return;
    setPdfBusy('summary');
    try {
      const logoImg = await loadImage(LOGO_SRC);
      const doc = buildReportPdf(data, {
        logo: logoImg ? (rasterize(logoImg)?.image ?? logoImg) : null, preparedBy: preparedBy(),
      });
      doc.save(reportFileName(data.project));
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfBusy(null);
    }
  };

  /* Photos are decoded here and rasterized to canvases before the builder runs. Handing jsPDF an
     <img> makes it re-fetch each one with a synchronous XHR at build time — blocking, and fatal to
     the whole PDF if one fetch fails — whereas a canvas is embedded from memory (see rasterize in
     reportPdf.ts). crossOrigin is required for that canvas read: without it the bitmap is tainted
     and rasterize returns null. Either failure counts the photo as missing and leaves it out
     rather than failing the download; loads run a few at a time so sixty photos don't open sixty
     connections at once on a tablet. */
  const generateDetailedPDF = async () => {
    if (!data || pdfBusy) return;
    if (assetsMissingDescription(data.facilityAssessments).length) return;
    setPdfBusy('detailed');
    try {
      const all = data.facilityAssessments.flatMap(fa =>
        SYSTEM_KEYS.flatMap(k => fa[k].assets.flatMap(a => a.photos ?? [])));
      const PARALLEL = 6;
      const photos: PhotoMap = {};
      for (let i = 0; i < all.length; i += PARALLEL) {
        await Promise.all(all.slice(i, i + PARALLEL).map(async ph => {
          const img = await loadImage(ph.url, 'anonymous');
          const bitmap = img ? rasterize(img) : null;
          if (bitmap) photos[ph.id] = bitmap;
        }));
      }
      const missing = all.length - Object.keys(photos).length;

      // The logo/mark assets are same-origin; the site photo is a Storage URL like any facility
      // photo, so it needs the same 'anonymous' CORS mode to keep rasterize() from tainting.
      const [logoImg, markImg, markWhiteImg, sitePhotoImg] = await Promise.all([
        loadImage(LOGO_SRC), loadImage(MARK_SRC), loadImage(MARK_WHITE_SRC),
        data.project.sitePhotoUrl ? loadImage(data.project.sitePhotoUrl, 'anonymous') : Promise.resolve(null),
      ]);
      const doc = buildDetailedReportPdf(data, {
        logo: logoImg ? (rasterize(logoImg)?.image ?? logoImg) : null,
        mark: markImg ? (rasterize(markImg)?.image ?? markImg) : null,
        markWhite: markWhiteImg ? (rasterize(markWhiteImg)?.image ?? markWhiteImg) : null,
        sitePhoto: sitePhotoImg ? rasterize(sitePhotoImg) ?? undefined : undefined,
        preparedBy: preparedBy(), photos,
      });
      doc.save(detailedReportFileName(data.project));
      if (missing > 0) {
        message.warning(`${missing} photo${missing === 1 ? '' : 's'} could not be loaded and ${missing === 1 ? 'was' : 'were'} left out of the report.`);
      }
    } catch (err) {
      console.error('Detailed PDF generation failed:', err);
      alert('Failed to generate the detailed PDF. Please try again.');
    } finally {
      setPdfBusy(null);
    }
  };

  // Blank (not a spinner) while a fast cache read is in flight; spinner only once it's proven slow.
  if (loading) return slow ? <div style={{ padding: '80px 0', textAlign: 'center' }}><Spin size="large" /></div> : null;
  if (error || !data) return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <Alert
        type="error"
        showIcon
        message={error || 'No data found.'}
        action={<Button size="small" onClick={() => navigate(-1)}>Go back</Button>}
      />
    </div>
  );

  const { project, facilityAssessments } = data;
  const totalAssets = facilityAssessments.reduce(
    (n, fa) => n + SYSTEM_KEYS.reduce((k, key) => k + fa[key].assets.length, 0), 0);
  const missingClass = assetsMissingDescription(facilityAssessments);
  const blockReport = missingClass.length > 0;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <Space className="no-print" style={{ marginBottom: 20 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/projects/${id}`)}>
          Back to project
        </Button>
        <Segmented
          value={view}
          onChange={v => setView(v as 'summary' | 'detailed')}
          options={[{ label: 'Summary', value: 'summary' }, { label: 'Detailed', value: 'detailed' }]}
        />
        <Button type="primary" icon={<FilePdfOutlined />} onClick={generatePDF} loading={pdfBusy === 'summary'} disabled={blockReport}>
          Summary PDF
        </Button>
        <Button
          icon={<FileImageOutlined />}
          onClick={generateDetailedPDF}
          loading={pdfBusy === 'detailed'}
          disabled={totalAssets === 0 || blockReport}
        >
          Detailed PDF
        </Button>
        <Button icon={<PrinterOutlined />} onClick={() => window.print()} disabled={blockReport}>
          Print
        </Button>
      </Space>

      {blockReport && (
        <Alert
          className="no-print"
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
          message="Description required before this report can be generated"
          description={(
            <>
              Set a description on the following asset{missingClass.length === 1 ? '' : 's'}:
              <ul style={{ margin: '6px 0 0', paddingInlineStart: 20 }}>
                {missingClass.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </>
          )}
        />
      )}

      <div className="report-sheet">
        {/* Letterhead — same composition as the PDF cover and the formal memo template. */}
        <div className="report-cover">
          <img className="report-cover__logo" src={LOGO_SRC} alt="Sustainability-Pros" />
          <div className="report-cover__rule" />
          <div className="report-cover__kicker">{view === 'detailed' ? 'Facility Assessment — Detailed' : 'Project Report'}</div>
          <div className="report-cover__title">{project.name}</div>
          {project.address && <div className="report-cover__meta">{project.address}</div>}
          <div className="report-cover__meta">
            Prepared by {organization?.name || currentUser?.displayName || currentUser?.email || '—'}
            {' · '}
            {new Date().toLocaleDateString()}
          </div>
        </div>

        {/* Detailed view — the per-asset cards, the HTML twin of the detailed PDF. */}
        {view === 'detailed' && <ReportDetailedView data={data} />}

        {/* Facility Assessments — summary tables */}
        {view === 'summary' && facilityAssessments.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <SectionTitle icon={<BankOutlined />}>Facility Asset Assessments</SectionTitle>
            {facilityAssessments.map(fa => (
              <div key={fa.id} className="report-section" style={{ marginBottom: 28 }}>
                <Typography.Text strong style={{ fontSize: 15 }}>{fa.buildingName || 'Untitled'}</Typography.Text>
                <div style={{ margin: '6px 0 12px' }}>
                  {/* Nothing assessed → ceiling is 0; read "Not assessed", never "0 / 0 — Failed"
                      (getBuildingRating(0, 0) is 'Failed'). Mirrors the PDF and detailed view. */}
                  {maxPossibleScore(fa) > 0 ? (
                    <>
                      <Typography.Text strong>Overall score:</Typography.Text>{' '}
                      {Math.round((fa.overallScore ?? 0) * 10) / 10} / {maxPossibleScore(fa)}{' '}
                      {ratingTag(getBuildingRating(fa.overallScore ?? 0, maxPossibleScore(fa)))}
                    </>
                  ) : (
                    <>
                      <Typography.Text strong>Overall:</Typography.Text>{' '}
                      {ratingTag('Not assessed')}
                    </>
                  )}
                </div>
                <table className="report-table" style={{ marginBottom: 16 }}>
                  <thead>
                    <tr><th>System</th><th>Score</th><th>Rating</th><th>Assets</th></tr>
                  </thead>
                  <tbody>
                    {SYSTEM_KEYS.map(k => (
                      <tr key={k}>
                        <td>{SYSTEM_LABELS[k]}</td>
                        <td className="num">{fa[k].score != null ? Math.round((fa[k].score ?? 0) * 10) / 10 : '—'}</td>
                        <td>{ratingTag(fa[k].rating) ?? '—'}</td>
                        <td className="num">{fa[k].assets.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Per-system asset tables */}
                {SYSTEM_KEYS.map(k => {
                  const sys = fa[k];
                  if (sys.assets.length === 0) return null;
                  return (
                    <div key={k} style={{ marginBottom: 16 }}>
                      <div style={{
                        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                        borderBottom: '1px solid #e0e4e6', paddingBottom: 4, marginBottom: 8,
                      }}>
                        <Typography.Text strong>{SYSTEM_LABELS[k]} Assets</Typography.Text>
                        <Button
                          className="no-print" size="small" icon={<DownloadOutlined />}
                          onClick={() => exportSystemCsv(fa.buildingName ?? '', k, sys.assets)}
                        >
                          Export CSV
                        </Button>
                      </div>
                      <div className="report-scroll">
                        <table className="report-table report-table--dense">
                          <thead>
                            <tr><th>ID</th><th>Description</th><th>Location</th><th>Age</th><th>RSL</th><th>Visual</th><th>Operational</th><th>Score</th><th>Rating</th></tr>
                          </thead>
                          <tbody>
                            {sys.assets.map(a => (
                              <tr key={a.id}>
                                <td>{a.assetId || '—'}</td>
                                <td>{a.description || '—'}</td>
                                <td>{a.location || '—'}</td>
                                <td className="num">{a.age != null ? `${a.age}yr` : '—'}</td>
                                <td className="num">{a.remainingServiceLife != null ? `${a.remainingServiceLife}yr` : '—'}</td>
                                <td>{a.visualInspection ?? '—'}</td>
                                <td>{a.operationalTest ?? '—'}</td>
                                <td className="num strong">{isAssetAssessed(a) ? Math.round((a.score ?? 0) * 10) / 10 : '—'}</td>
                                <td>{ratingTag(a.rating, 'sm') ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}

                {/* Findings & Recommendations — always at least one line. */}
                <div style={{ marginBottom: 16 }}>
                  <Typography.Text strong style={{ display: 'block', borderBottom: '1px solid #e0e4e6', paddingBottom: 4, marginBottom: 8 }}>
                    Findings &amp; Recommendations
                  </Typography.Text>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {generateRecommendations(fa).map((r, i) => <li key={i} style={{ marginBottom: 4 }}>{r}</li>)}
                  </ul>
                </div>

                {/* Capital planning — assets with a known RSL, soonest replacement first. */}
                {(() => {
                  const cap = capitalPlanRows(fa);
                  if (!cap.length) return null;
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <Typography.Text strong style={{ display: 'block', borderBottom: '1px solid #e0e4e6', paddingBottom: 4, marginBottom: 8 }}>
                        Capital Planning — by Remaining Service Life
                      </Typography.Text>
                      <div className="report-scroll">
                        <table className="report-table report-table--dense">
                          <thead>
                            <tr><th>RSL</th><th>System</th><th>ID</th><th>Description</th><th>Location</th><th>Age</th><th>Rating</th></tr>
                          </thead>
                          <tbody>
                            {cap.map((r, i) => (
                              <tr key={i}>
                                <td className="num strong">{r.rsl}yr</td>
                                <td>{r.system}</td>
                                <td>{r.assetId}</td>
                                <td>{r.description}</td>
                                <td>{r.location}</td>
                                <td className="num">{r.age != null ? `${r.age}yr` : '—'}</td>
                                <td>{ratingTag(r.rating, 'sm') ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {view === 'summary' && facilityAssessments.length === 0 && (
          <div style={{ textAlign: 'center', color: TONE.muted, padding: '56px 0' }}>
            <InboxOutlined style={{ fontSize: 40, display: 'block', marginBottom: 10 }} />
            <div>No data recorded for this project yet.</div>
          </div>
        )}

        <div style={{
          textAlign: 'center', color: TONE.muted, fontSize: 12,
          borderTop: '1px solid #e0e4e6', paddingTop: 14, marginTop: 24,
        }}>
          MEP Field Tools — {project.name}
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
